// SPÅR 1a — anonymous activity detail (V1-baserad ersättning).
//
// Replaces V2-only `AnonymousOpenMatchDetail.tsx` once Spår 1b raderar
// `public_open_matches_view`. Read-only view av en V1-aktivitet för
// en oinloggad besökare. Spec D15 — Route: `/aktiviteter/:id`.
//
// Click handling:
//   - "Anmäl" → AuthRequiredModal med intent='join_open_match' och
//     returnUrl=`/aktiviteter/:id`
//   - Back → `/aktiviteter`
//
// landing_events: 'anon_activity_detail_view' på mount.

import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { format, parseISO } from "date-fns";
import { sv } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { usePublicOrgContext } from "@/contexts/PublicOrgContext";
import { useAuthRequired } from "@/contexts/AuthRequiredContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Users, Clock } from "lucide-react";

interface PublicActivityRow {
  id: string;
  organization_id: string;
  title: string | null;
  description: string | null;
  total_spots: number;
  participant_count: number;
  min_level: number | null;
  max_level: number | null;
  status: string;
  date: string;
  start_time: string;
  end_time: string;
  court_id: string | null;
  activity_type_id: string | null;
  match_type: string | null;
  gender_filter: string | null;
  cover_image_url: string | null;
  min_participants: number | null;
  currency: string;
  created_at: string;
}

const STATUS_COLORS: Record<string, string> = {
  open: "bg-green-100 text-green-800",
};

export default function AnonymousActivityDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { org: publicOrg } = usePublicOrgContext();
  const { requireAuth } = useAuthRequired();
  // T2v2/G-053: authenticated users get the logged-in detail view (redirect
  // return lives AFTER all hooks — rules-of-hooks).
  const { loading: authLoading, isAuthenticated } = useAuth();

  const { data: activity, isLoading } = useQuery({
    queryKey: ["anon-activity-detail", id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase
        .from("public_activities_view" as any)
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as PublicActivityRow | null;
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (!publicOrg?.id || !id) return;
    supabase
      .from("landing_events" as any)
      .insert({
        event_type: "anon_activity_detail_view",
        hostname: typeof window !== "undefined" ? window.location.hostname : null,
        organization_id: publicOrg.id,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
      } as any)
      .then(() => {
        /* best-effort */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicOrg?.id, id]);

  const handleJoin = () => {
    requireAuth({
      intent: "join_open_match",
      returnUrl: `/aktiviteter/${id}`,
    });
  };

  // T2v2/G-053: logged-in users land on /activities/:id (which also renders
  // private activities — the anon view can't).
  if (!authLoading && isAuthenticated) {
    return <Navigate to={id ? `/activities/${id}` : "/play"} replace />;
  }

  if (isLoading) {
    return (
      <div className="max-w-md mx-auto space-y-4 pt-4 p-4">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-60 w-full" />
      </div>
    );
  }

  if (!activity) {
    return (
      <div className="max-w-md mx-auto pt-8 text-center text-muted-foreground p-4">
        {t("activities.detail.notFound")}
      </div>
    );
  }

  const isFull = activity.participant_count >= activity.total_spots;

  return (
    <div className="max-w-md mx-auto space-y-4 pb-8 p-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => navigate("/aktiviteter")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-lg font-semibold flex-1 truncate">
          {activity.title || t("activities.defaultTitle")}
        </h1>
        <Badge variant="outline" className={STATUS_COLORS[activity.status] ?? ""}>
          {t(`activities.status.${activity.status}`)}
        </Badge>
      </div>

      {/* Activity info */}
      <Card>
        <CardContent className="pt-4 space-y-3">
          {/* Time — V1 har alltid specifik date+start_time+end_time */}
          <div className="flex items-center gap-3 text-sm">
            <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
            <span>
              {format(parseISO(`${activity.date}T${activity.start_time}`), "EEEE d MMMM HH:mm", { locale: sv })}
              {activity.end_time ? `–${activity.end_time.slice(0, 5)}` : ""}
            </span>
          </div>

          {/* Spots — count only, no names */}
          <div className="flex items-center gap-3 text-sm">
            <Users className="h-4 w-4 text-muted-foreground shrink-0" />
            <span>
              {activity.participant_count}/{activity.total_spots} {t("activities.form.players")}
            </span>
          </div>

          {/* Level */}
          {(activity.min_level || activity.max_level) && (
            <div className="text-sm text-muted-foreground">
              {t("activities.level")}: {activity.min_level ?? "—"}–{activity.max_level ?? "—"}
            </div>
          )}

          {/* Description */}
          {activity.description && (
            <p className="text-sm text-muted-foreground border-t pt-3">
              {activity.description}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Anonymous notice */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Users className="h-4 w-4" />
            {t("activities.detail.participants")} ({activity.participant_count}/{activity.total_spots})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {t(
              "activities.anonymous.participantsHidden",
              "Skapa konto för att se vilka som är med och chatta med dem.",
            )}
          </p>
        </CardContent>
      </Card>

      {/* Action */}
      {!isFull && activity.status === "open" && (
        <Button className="w-full" onClick={handleJoin}>
          {t("activities.join")}
        </Button>
      )}
      {isFull && (
        <Button className="w-full" variant="outline" disabled>
          {t("activities.full")}
        </Button>
      )}

      <NoIndexMeta />
    </div>
  );
}

function NoIndexMeta() {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);
  return null;
}
